import { useState } from 'react';
import { ExtraBase, QuoteApiModel, QuoteExtraApiModel, SmartSendItemApiModel } from '~/lib/api/smartsend/types';
import { ToUniqueExtrasList } from '../../lib/helpers/ToUniqueExtras';
import { CoverExtra, ExtrasWithCustomUi } from '../ExtrasHelper';
import Loading from '../../../../components/loading/Loading';
import { ExtraRow } from './actions/Extras';
import { getSelectedQuoteOption } from './generateProtectionKey';
import ServiceInformationModal from './item/ServiceInformationModal';
import { ExtraAlwaysIncluded } from '../../lib/helpers/ExtraAlwaysIncluded';
import { IsExtraPricePending } from '../../lib/helpers/ExtraPricePending';
import { P2GBox, P2GText, P2GAlert, P2GGrid, P2GLink } from '@parcel2go/core-ui';
import { toDisplayCurrency } from '~/components/currency/toDisplayCurrency';
import { Trans, useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import { useTaxName } from '@parcel2go/npm-p2g-app';
import useOnLoad from '~/routes/use-on-load';

interface props {
  onUpdate?: (extras: ExtraBase[]) => void;
  getQuote: () => Promise<QuoteApiModel>;
  currentSelection: ExtraBase[];
  showNonFoundMessage?: boolean;
  item: SmartSendItemApiModel;
  extrasWhiteList?: string[];
  extrasBlackList?: string[];
  wrapper: (content: React.ReactNode) => React.ReactNode;
  hideLoader?: boolean;
}

export const ExtrasSelection: React.FC<props> = (props) => {
  const [loading, setLoading] = useState(true);
  const [quote, setQuote] = useState<QuoteApiModel>();
  const [selectedExtras, setSelectedExtras] = useState<QuoteExtraApiModel[]>([]);
  const [unavailableExtras, setUnavailableExtras] = useState<ExtraBase[]>([]);
  const [options, setOptions] = useState<{ extra: QuoteExtraApiModel; included: boolean }[]>([]);
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  const taxName = useTaxName();

  useOnLoad(() => {
    async function getData() {
      const r = await props.getQuote();
      setQuote(r);
      if (r) {
        const qo = getSelectedQuoteOption(props.item, r) || r.Options[0];
        // Possible Extras
        const fullList = qo.AvailableExtras.concat(qo.Extras);

        // Show only visible, and not optional (eg those which end with 'Only')
        setOptions(
          fullList
            .filter(
              (x) =>
                !ExtrasWithCustomUi.includes(x.Id) &&
                (!props.extrasWhiteList || props.extrasWhiteList.includes(x.Id)) &&
                (!props.extrasBlackList || !props.extrasBlackList.includes(x.Id))
            )
            .map((x) => ({ extra: x, included: ExtraAlwaysIncluded(x.Id, r.Service) }))
        );

        // Set selections
        const selection = fullList.filter((x) => !!props.currentSelection.find((y) => y.Id === x.Id));
        setSelectedExtras(selection);

        // Set extras that are no longer available for this service
        const unavailable = props.currentSelection.filter((x) => !fullList.find((y) => y.Id === x.Id));
        setUnavailableExtras(unavailable);
      }
      setLoading(false);
    }
    getData();
  });

  const toggle = (extra: QuoteExtraApiModel) => {
    let newExtras = [...selectedExtras];
    if (newExtras.find((x) => x.Id === extra.Id)) {
      newExtras = newExtras.filter((x) => x.Id !== extra.Id);
    } else {
      newExtras.push(extra);
    }
    setSelectedExtras(newExtras);
    props.onUpdate && props.onUpdate(newExtras);
  };

  const renderPrice = (extra: QuoteExtraApiModel, price: number, currencyCode: string) => {
    // A pending zero is not free - the courier prices it once the shipment is re-rated with the option
    // on - so it must never fall through to the FREE branch below.
    if (IsExtraPricePending(extra)) {
      return t('EXTRA_PRICE_PENDING', { defaultValue: 'Price confirmed when booked' });
    }

    return price === 0 ? (
      t('FREE_PRICE', { defaultValue: 'FREE' })
    ) : (
      <Trans
        t={t}
        i18nKey={`PRICE_EXCLUDING_TAX`}
        values={{ amount: toDisplayCurrency(currencyCode, price), taxName: taxName }}
        defaults={`+{{amount}} exc {{taxName}}`}
      />
    );
  };

  if (loading || !quote) {
    return !props.hideLoader ? <Loading /> : null;
  }

  if (!props.showNonFoundMessage && !options?.length) {
    return null;
  }

  return props.wrapper(
    <P2GBox sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {unavailableExtras.length > 0 && (
        <P2GAlert color="error">
          <P2GGrid container wrap="nowrap" gap={2}>
            <P2GGrid item flexGrow={1}>
              <P2GText variant="body2" component="p">
                <Trans
                  t={t}
                  i18nKey="EXTRAS_NOT_AVAILABLE_FOR_SERVICE_ALERT"
                  values={{ extras: ToUniqueExtrasList(t, unavailableExtras) }}
                  defaults="The following extras are unavailable for this service:<br /><strong>{{extras}}</strong>"
                />
              </P2GText>
            </P2GGrid>
            <P2GGrid item flexShrink={1}>
              <P2GLink
                onClick={() => {
                  setUnavailableExtras([]);
                  props.onUpdate(selectedExtras);
                }}
              >
                {t('REMOVE_LINK', { defaultValue: 'Remove' })}
              </P2GLink>
            </P2GGrid>
          </P2GGrid>
        </P2GAlert>
      )}
      {props.showNonFoundMessage && options.length === 0 ? (
        <P2GBox>
          <P2GText>
            {t('NO_AVAILABLE_EXTRAS', { defaultValue: 'There are no available extras for this service.' })}
          </P2GText>
        </P2GBox>
      ) : (
        <P2GBox>
          <P2GGrid container direction="column" gap={2}>
            {options.map((o) => (
              <P2GGrid item key={o.extra.Id}>
                <ExtraRow
                  extra={o.extra}
                  disabled={o.included}
                  onClick={() => toggle(o.extra)}
                  selected={!!selectedExtras.find((x) => x.Id === o.extra.Id)}
                  price={<>({renderPrice(o.extra, o.extra.Price, quote.CurrencyCode)})</>}
                >
                  {o.extra.Id === CoverExtra && props.item && (
                    <ServiceInformationModal
                      itemId={props.item.Id}
                      service={props.item.Service}
                      recipientIsoCode={props.item.Recipient.CountryIsoCode}
                      recipientSubdivision={props.item.Recipient.CountrySubdivision}
                      senderIsoCode={props.item.Sender.CountryIsoCode}
                      senderSubdivision={props.item.Sender.CountrySubdivision}
                      dims={props.item.PackageDimensions}
                      label={t('VIEW_PROTECTION_LIMITS_LABEL', { defaultValue: 'View protection limits' })}
                      currencyCode={quote.CurrencyCode}
                    />
                  )}
                </ExtraRow>
              </P2GGrid>
            ))}
          </P2GGrid>
        </P2GBox>
      )}
    </P2GBox>
  );
};
